import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getSupabaseClient } from '../../data/supabase-client';
import { reportError } from '../../lib/monitoring';
import './auth.css';

/**
 * The hosted web password-reset page (D-005).
 *
 * HOW A RECOVERY LINK ACTUALLY ARRIVES:
 *
 *   The email links to Supabase, which verifies the token and redirects here
 *   with an IMPLICIT-FLOW FRAGMENT:
 *
 *     /reset-password#access_token=…&refresh_token=…&type=recovery
 *
 *   supabase-js consumes that fragment during initialisation — which is why
 *   `detectSessionInUrl` must stay true — establishes a session, and emits a
 *   PASSWORD_RECOVERY event. There is NO `?code=` query parameter unless the
 *   client is configured for the PKCE flow, which this one is not.
 *
 *   An earlier version looked only for `?code=`, found none, and declared every
 *   valid link invalid. Both paths are handled now: the PKCE exchange is kept
 *   for the day the flow changes, and the implicit path is what runs today.
 *
 * No native deep link is built or implied — the reset always completes on the
 * web, and Brandon then signs into the native app with the new password.
 */

/**
 * Three states, for the same reason the auth gate has three: "we do not know
 * yet" is not "invalid". Rendering the invalid-link message while the client is
 * still parsing the fragment flashes a false failure at the exact moment the
 * link is working.
 */
type RecoveryState = 'checking' | 'ready' | 'invalid' | 'done';

/**
 * Does this URL carry a live recovery hand-off from Supabase?
 *
 * A valid implicit link arrives as `#access_token=…&type=recovery`. A CONSUMED
 * one does not: Supabase redirects here with `#error=…&error_code=otp_expired`
 * instead, which is exactly the difference this function exists to see.
 */
function hasRecoveryHandoff(href: string): boolean {
  const hash = new URL(href).hash.replace(/^#/, '');
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  return params.get('type') === 'recovery' && params.get('access_token') !== null;
}

/**
 * **CAPTURED AT MODULE LOAD, BEFORE THE CLIENT CAN CONSUME IT.**
 *
 * supabase-js strips the fragment while it initialises, and it may finish
 * before this screen ever renders. Reading the URL later would therefore find
 * nothing and reject a perfectly good link — the regression this screen has
 * already suffered once. This module is imported when the bundle evaluates,
 * which is earlier than any client work, so the observation is taken while the
 * evidence still exists.
 */
const RECOVERY_HANDOFF_AT_LOAD =
  typeof window === 'undefined' ? false : hasRecoveryHandoff(window.location.href);

export function ResetPasswordScreen() {
  const client = getSupabaseClient();
  // Derived rather than set inside the effect: with no client there is nothing
  // to check, so 'checking' would be a state we could never leave honestly.
  const [recovery, setRecovery] = useState<RecoveryState>(client ? 'checking' : 'invalid');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // No client at all — nothing to verify. The initial state already says so.
    if (!client) return;

    let cancelled = false;

    // Subscribed BEFORE the session is read, so a PASSWORD_RECOVERY event that
    // fires during initialisation is not missed. **The `|| session` that used
    // to sit here is gone: it accepted INITIAL_SESSION and SIGNED_IN, which is
    // how an ordinary saved login became proof of a password recovery.**
    const { data: subscription } = client.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY') setRecovery('ready');
    });

    async function establish() {
      if (!client) return;
      try {
        // PKCE path, kept for if the flow is ever switched. A completed
        // exchange is itself proof that THIS load carried a recovery code.
        const code = new URL(window.location.href).searchParams.get('code');
        let exchanged = false;
        if (code) {
          const { error: exchangeError } = await client.auth.exchangeCodeForSession(code);
          if (exchangeError) throw exchangeError;
          exchanged = true;
        }

        // getSession() waits for initialisation, by which point a valid
        // fragment has been consumed and its session exists.
        const { data } = await client.auth.getSession();
        if (cancelled) return;

        // **FRESHNESS AND VALIDITY, BOTH REQUIRED.**
        //
        // The hand-off proves this navigation carried a recovery; the session
        // proves the token behind it actually validated. Either alone is
        // wrong: a session alone is just somebody already signed in — the
        // defect — and a hand-off alone would show the form for an expired
        // token that established nothing.
        const fresh = (RECOVERY_HANDOFF_AT_LOAD || exchanged) && data.session !== null;

        // Never downgrade a recovery the listener already confirmed.
        setRecovery((previous) => (previous === 'ready' ? previous : fresh ? 'ready' : 'invalid'));
      } catch (caught) {
        // The context string is fixed and no URL, token or fragment is passed —
        // nothing here can carry credential material into an error report.
        reportError(caught, 'auth.resetPasswordRecovery');
        if (!cancelled) setRecovery((previous) => (previous === 'ready' ? previous : 'invalid'));
      }
    }

    void establish();

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [client]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !client) return;
    if (password !== confirmation) {
      setError('Those two passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }

    setBusy(true);
    setError(null);
    // Runs against the recovery session established above. The form is not
    // rendered in any other state, so there is no path that reaches this
    // without one.
    const { error: updateError } = await client.auth.updateUser({ password });
    setBusy(false);

    if (updateError) {
      reportError(updateError, 'auth.updatePassword');
      setError('Could not update the password. Ask for a fresh reset link.');
      return;
    }

    // **THE RECOVERY SESSION IS ENDED, NOT KEPT.**
    //
    // It used to be kept and Brandon was dropped onto Projects, which is what
    // left a signed-in browser behind — and a signed-in browser was, until
    // now, all this page ever asked for. Ending it closes that door and
    // matches what the flow already assumes: the reset finishes on the web and
    // he signs into the phone with the new password.
    //
    // `scope: 'local'` deliberately: this ends THIS browser only. A global
    // sign-out would reach beyond the browser that did the recovery, which is
    // not what was asked for and not this page's business.
    try {
      await client.auth.signOut({ scope: 'local' });
    } catch (caught) {
      // The password DID change. Failing to tidy the browser session must not
      // be reported as a failed reset — that would send him back to request
      // another link he does not need.
      reportError(caught, 'auth.resetPasswordSignOut');
    }

    setRecovery('done');
  }

  return (
    <main className="cl-screen cl-centered auth-screen">
      <header className="auth-header">
        <h1 className="auth-wordmark">Choose a new password</h1>
      </header>

      {recovery === 'checking' ? (
        // Deliberately empty. A spinner or a message here would flash for a few
        // milliseconds on every valid link.
        <div aria-busy="true" data-testid="recovery-check" />
      ) : null}

      {recovery === 'done' ? (
        <>
          <p className="cl-notice" role="status">
            Password updated. Return to Crewlink and sign in with your new password.
          </p>
          <p className="auth-back">
            <Link className="cl-link" to="/sign-in">
              Back to sign in
            </Link>
          </p>
        </>
      ) : null}

      {recovery === 'invalid' ? (
        <>
          <p className="cl-notice" role="status">
            This reset link is not valid or has already been used. Request a new one and try again.
          </p>
          <p className="auth-back">
            <Link className="cl-link" to="/forgot-password">
              Request a new link
            </Link>
          </p>
        </>
      ) : null}

      {recovery === 'ready' ? (
        <form onSubmit={(event) => void handleSubmit(event)} noValidate>
          <label className="cl-field">
            <span className="cl-label">New password</span>
            <input
              className="cl-input"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </label>

          <label className="cl-field">
            <span className="cl-label">Confirm new password</span>
            <input
              className="cl-input"
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={busy}
            />
          </label>

          {error ? (
            <p className="cl-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="auth-actions">
            <button className="cl-button" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save new password'}
            </button>
          </div>
        </form>
      ) : null}
    </main>
  );
}
